import { memo } from 'react'
import Funnel from '@/components/funnel'
import { InfoIcon } from '@/components/icons'
import useAdsSelectionStore from '@/store/adsSelectionStore'
import { FUNNEL_STAGES } from '@/utils/ad-campaign-constants'
import { useFunnel } from '@/components/mediaBuying/adCampaign/useFunnel'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'

const RangeRecommendation = ({ getSelectedCreativesForAudience, stage, audience }) => {
    const creativeIds = getSelectedCreativesForAudience(audience.id, stage.id)
    const { recommendedMinCreatives, recommendedMaxCreatives } = FUNNEL_STAGES[stage.id]
    const showRecommendations =
        creativeIds.length < recommendedMinCreatives || creativeIds.length > recommendedMaxCreatives

    if (!showRecommendations) return null

    return (
        <div>
            Please add {recommendedMinCreatives}-{recommendedMaxCreatives} ads for {audience.name} in {stage.name} stage
        </div>
    )
}

const Recommendations = memo(({ getSelectedCreativesForAudience }) => {
    const { selectedAudience, selectedStage, getCurrentRecommendation, getWarningForCurrent } = useAdsSelectionStore()
    const { getStepContentData } = useWizard()
    const { selectedObjective } = getStepContentData('campaignObjective')

    // Get current recommendation for selected audience
    const currentRecommendation = getCurrentRecommendation()

    // Get current creative count for the selected audience
    const currentCreativeCount =
        selectedAudience && selectedStage
            ? getSelectedCreativesForAudience(selectedAudience.id, selectedStage.id).length
            : 0

    // Get warning for current selection
    const currentWarning = getWarningForCurrent(currentCreativeCount)

    // Show warning if current selection is out of range
    const hasWarnings = currentWarning !== null

    const { availableStages, audienceSelections } = useFunnel()

    return (
        <div className="tw:space-y-6">
            {/* Funnel Visualization */}
            <div className="tw:flex tw:justify-center">
                <Funnel availableStages={availableStages} audienceSelections={audienceSelections} />
            </div>

            {/* Recommendations - Show only for currently selected audience */}
            {currentRecommendation && (
                <div>
                    <div className="tw:flex tw:items-center tw:gap-2 tw:mb-3">
                        <InfoIcon className="tw:size-4 tw:text-blue-400" />
                        <span className="tw:!text-white tw:!font-semibold tw:!text-sm">Recommendations</span>
                    </div>
                    <div className="tw:text-sm">
                        <RangeRecommendation
                            getSelectedCreativesForAudience={getSelectedCreativesForAudience}
                            stage={selectedStage}
                            audience={selectedAudience}
                        />
                        ({currentRecommendation.stageName.toLowerCase()} stage).
                    </div>
                </div>
            )}

            {/* Warning Message - Show specific info for current selection */}
            {hasWarnings && currentWarning && (
                <div className="tw:space-y-2">
                    <div className="tw:text-yellow-400 tw:text-sm tw:leading-relaxed">
                        Using more or fewer creatives than recommended may lead to a slower learning phase and/or higher
                        costs.
                    </div>
                </div>
            )}
        </div>
    )
})

Recommendations.displayName = 'Recommendations'

export default Recommendations
